/**
 * ConfirmAppointmentPage (Jose 2026-10-20) — the client's "pick the real one" page.
 *
 * Opened from the texted link (/confirm/:token). The Fresha → SLIK migration left some clients with
 * two copies of one upcoming appointment; this shows them side by side and lets the client tap the
 * one that's correct. Tapping resolves the confirmation; a server trigger cancels the other copy.
 * Self-contained styling so it looks the same wherever it's hosted. Mirrors ClaimOfferPage.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apptConfirmationService, ApptConfirmation, ConfirmCandidate } from '../services/apptConfirmationService';

type Loaded =
  | { kind: 'loading' }
  | { kind: 'notfound' }
  | { kind: 'ready'; conf: ApptConfirmation };

const wrap: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16, background: 'linear-gradient(160deg,#0f172a 0%,#1e1b4b 60%,#312e81 100%)',
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
};
const card: React.CSSProperties = {
  width: '100%', maxWidth: 440, background: '#fff', borderRadius: 24,
  boxShadow: '0 24px 60px rgba(0,0,0,.4)', overflow: 'hidden',
};

// Date '2026-10-21' → "Wed, Oct 21"
function dayLabel(d: string): string {
  try {
    const dt = new Date(`${d}T12:00:00`);
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return d; }
}
// Time '14:30' or '2:30 PM' → "2:30 PM"
function timeLabel(t: string): string {
  const s = String(t || '').trim();
  if (/am|pm/i.test(s)) return s.toUpperCase().replace(/\s+/g, ' ');
  const [h, m] = s.split(':').map(Number);
  if (Number.isNaN(h)) return s;
  const period = h >= 12 ? 'PM' : 'AM';
  const hh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hh}:${String(m || 0).padStart(2, '0')} ${period}`;
}

const ConfirmAppointmentPage: React.FC = () => {
  const { token = '' } = useParams();
  const [state, setState] = useState<Loaded>({ kind: 'loading' });
  const [saving, setSaving] = useState<string | null>(null);
  const [done, setDone] = useState<ConfirmCandidate | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let alive = true;
    apptConfirmationService.getByToken(token).then((conf) => {
      if (!alive) return;
      setState(conf ? { kind: 'ready', conf } : { kind: 'notfound' });
    }).catch(() => alive && setState({ kind: 'notfound' }));
    return () => { alive = false; };
  }, [token]);

  const pick = async (c: ConfirmCandidate) => {
    if (saving) return;
    setSaving(c.apptId); setError('');
    try {
      await apptConfirmationService.resolve(token, c.apptId);
      setDone(c);
    } catch (e: any) {
      setError(e?.message || 'Could not save that. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  const body = useMemo(() => {
    if (state.kind === 'loading') return <Centered>Loading your appointment…</Centered>;
    if (state.kind === 'notfound') return <Centered>This confirmation link isn’t valid.</Centered>;

    const conf = state.conf;

    // Already resolved (this visit or a prior tap)
    const kept = done || (conf.status === 'resolved'
      ? conf.candidates.find(c => c.apptId === conf.keptApptId) || null
      : null);
    if (kept) {
      return (
        <div style={{ padding: '40px 28px', textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>✅</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>You’re all set!</h1>
          <p style={{ color: '#475569', fontSize: 16, margin: 0 }}>
            We’ll see you <strong>{dayLabel(kept.date)}</strong> at <strong>{timeLabel(kept.time)}</strong>
            {kept.barberName ? <> with {String(kept.barberName).split(' ')[0]}</> : null}.
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 18 }}>The duplicate was removed. See you at SLIK. 💈</p>
        </div>
      );
    }

    return (
      <div>
        <div style={{ background: '#4f46e5', color: '#fff', padding: '18px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Hi {conf.clientName.split(' ')[0] || 'there'} 👋</div>
          <div style={{ fontSize: 13, opacity: .9, marginTop: 4, lineHeight: 1.4 }}>
            We switched booking systems and found two copies of your upcoming appointment. Tap the one that’s correct — we’ll remove the other.
          </div>
        </div>
        <div style={{ padding: '20px 20px 26px' }}>
          {error && (
            <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {conf.candidates.map((c) => (
              <button
                key={c.apptId}
                onClick={() => pick(c)}
                disabled={saving !== null}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start',
                  padding: '16px 18px', borderRadius: 16, border: '2px solid #c7d2fe',
                  background: saving === c.apptId ? '#4f46e5' : '#eef2ff', color: saving === c.apptId ? '#fff' : '#312e81',
                  cursor: saving !== null ? 'default' : 'pointer',
                  opacity: saving !== null && saving !== c.apptId ? .5 : 1, transition: 'all .15s', textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 18, fontWeight: 800 }}>{dayLabel(c.date)} · {timeLabel(c.time)}</span>
                <span style={{ fontSize: 14, fontWeight: 500, opacity: .85 }}>
                  {c.barberName ? `with ${String(c.barberName).split(' ')[0]}` : ''}{c.service ? ` · ${c.service}` : ''}
                </span>
                {saving === c.apptId && <span style={{ fontSize: 13, marginTop: 4 }}>Saving…</span>}
              </button>
            ))}
          </div>
          <p style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', marginTop: 16 }}>Tap the appointment you’re keeping.</p>
        </div>
      </div>
    );
  }, [state, done, error, saving]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ padding: '16px 24px 0', textAlign: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, color: '#6366f1' }}>SLIK BARBER LOUNGE</span>
        </div>
        {body}
      </div>
    </div>
  );
};

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: '48px 28px', textAlign: 'center', color: '#475569', fontSize: 16 }}>{children}</div>
);

export default ConfirmAppointmentPage;
